import type { Message, VisitorConfig } from '@chat/types';

export class VisitorApiError extends Error {
  constructor(public status: number, public code: string) {
    super(code);
  }
}

async function checkResponse(res: Response): Promise<void> {
  if (res.ok) return;
  const data = await res.json().catch(() => null) as { error?: string; code?: string } | null;
  throw new VisitorApiError(res.status, data?.code ?? data?.error ?? 'request_failed');
}

export interface InitResponse {
  ok: true;
  visitorId: string;
  conversationId: string;
  wsToken: string;
  config: VisitorConfig;
  messages: Message[];
}

/** widget script 通过 window.__CHAT_HOST 或 script src 派生 host。 */
export function detectHost(currentScript: HTMLScriptElement | null): string {
  const explicit = (window as unknown as { __CHAT_HOST?: string }).__CHAT_HOST;
  if (explicit) return explicit;
  if (currentScript?.src) {
    try {
      const u = new URL(currentScript.src);
      return u.origin;
    } catch {
      // ignore
    }
  }
  return window.location.origin;
}

export async function initVisitor(
  host: string,
  productSlug: string,
  fingerprint: string,
  hadStoredId = true,
): Promise<InitResponse> {
  const res = await fetch(`${host}/api/visitor/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({
      productSlug,
      fingerprint,
      hadStoredId,
      origin: window.location.origin,
      referrer: document.referrer || window.location.href,
    }),
  });
  await checkResponse(res);
  return (await res.json()) as InitResponse;
}

export async function refreshVisitor(
  host: string,
  wsToken: string,
): Promise<Pick<InitResponse, 'ok' | 'conversationId' | 'visitorId' | 'wsToken'>> {
  const res = await fetch(`${host}/api/visitor/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${wsToken}` },
    credentials: 'omit',
  });
  await checkResponse(res);
  return await res.json() as Pick<InitResponse, 'ok' | 'conversationId' | 'visitorId' | 'wsToken'>;
}

export async function fetchHistory(
  host: string,
  conversationId: string,
  wsToken: string,
  before?: number,
): Promise<Message[]> {
  const q = new URLSearchParams({ conversationId });
  if (before) q.set('before', String(before));
  const res = await fetch(`${host}/api/visitor/history?${q.toString()}`, {
    headers: { Authorization: `Bearer ${wsToken}` },
    credentials: 'omit',
  });
  await checkResponse(res);
  const data = (await res.json()) as { ok: boolean; messages: Message[] };
  return data.messages ?? [];
}

export async function uploadImage(
  host: string,
  wsToken: string,
  file: File,
): Promise<{ r2Key: string; mime: string; size: number }> {
  const res = await fetch(`${host}/api/visitor/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      'X-WS-Token': wsToken,
    },
    credentials: 'omit',
    body: file,
  });
  await checkResponse(res);
  const data = (await res.json()) as { ok: boolean; r2Key: string; mime: string; size: number };
  if (!data.ok) throw new Error('upload rejected');
  return { r2Key: data.r2Key, mime: data.mime, size: data.size };
}

export function imageUrl(host: string, r2Key: string): string {
  return `${host}/r2/${r2Key}`;
}

export function buildWsUrl(host: string, wsToken: string): string {
  const u = new URL('/ws/visitor', host);
  u.searchParams.set('t', wsToken);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return u.toString();
}
